import type { Options as IconOptions } from 'ol/style/Icon'
import { Icon, Style } from 'ol/style'

import ordersSelectionSpriteSheet from '@/resources/map-markers/orders_selection.png'
import ordersSelectionMinSpriteSheet from '@/resources/map-markers/orders_selection_min.png'

import { genericStyles } from '../../../genericStyles'
import ordersSelectionMinCoordinates from './orders_selection_min.json'
import ordersSelectionCoordinates from './orders_selection.json'

const orderSelectionStylesCache: Map<string, Style> = new Map()
const emptySet = new Set<string>()

export function getMarkerSelectionStyle(
  marker: uui.domain.ui.map.markers.Order,
  mode: uui.domain.ui.map.markers.MapStyles['order']['mode'],
  selectedOrderIds: Set<string> = emptySet,
  selected: boolean = false,
) {
  if (mode === 'off') return genericStyles.hidden

  const spriteSheetUrl = mode === 'on' ? ordersSelectionMinSpriteSheet : ordersSelectionSpriteSheet

  const ids = Object.keys(marker.ids).filter(orderStepId => !marker.ids[orderStepId].filtered)
  const partial = selected && ids.some(id => !selectedOrderIds.has(id))
  const selectionMode = selected ? (partial ? 'partial' : 'full') : 'none'

  const cacheId = `${mode}_${selectionMode}_${selected ? 'selected' : 'not-selected'}`

  if (orderSelectionStylesCache.has(cacheId)) {
    const cachedStyle = orderSelectionStylesCache.get(cacheId)

    if (!cachedStyle) {
      throw new Error(`Cannot find cached style with key ${cacheId}`)
    }

    return cachedStyle
  }

  let coordinates: uui.domain.ui.map.markers.GridPlacement = [0, 0, 0]

  const minCoordinates = ordersSelectionMinCoordinates.coordinates[
    '#458FFF'
  ] as uui.spriteSheet.ColorCoordinates<uui.spriteSheet.OrderSelectionCells>
  const maxCoordinates = ordersSelectionCoordinates.coordinates[
    '#458FFF'
  ] as uui.spriteSheet.ColorCoordinates<uui.spriteSheet.OrderSelectionCells>

  switch (selectionMode) {
    case 'full':
      coordinates =
        mode === 'on'
          ? [...minCoordinates.full, ordersSelectionMinCoordinates.side]
          : [...maxCoordinates.full, ordersSelectionCoordinates.side]
      break

    case 'partial':
      coordinates =
        mode === 'on'
          ? [...minCoordinates.partial, ordersSelectionMinCoordinates.side]
          : [...maxCoordinates.partial, ordersSelectionCoordinates.side]
      break

    case 'none':
    default:
      return genericStyles.hidden
  }

  const [x, y, cellSize] = coordinates

  const opts: IconOptions = {
    anchor: [0.5, 0.5],
    anchorOrigin: 'top-left',
    anchorYUnits: 'fraction',
    anchorXUnits: 'fraction',
  }

  const style = new Style({
    zIndex: selected ? 2 : mode === 'maximized' ? 1 : undefined,
    image: new Icon({
      src: spriteSheetUrl,
      crossOrigin: 'anonymous',

      size: [cellSize, cellSize],
      scale: 0.5,
      offset: [x, y],
      opacity: 1.0,
      rotation: 0.0,
      rotateWithView: true,

      ...opts,
    }),
  })

  orderSelectionStylesCache.set(cacheId, style)

  return style
}
